import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { modelsData } from '../data/car360Data';

export default function Car360Viewer({ modelName = 'bj30' }) {
  const { language } = useLanguage();
  const t = translations[language];
  // Get data for the selected model
  const currentModelData = modelsData[modelName] || modelsData['bj30'];
  const is360 = currentModelData.type === '360';

  // Supporting frames 0 to 36 based on user input (37 total frames effectively, or 0-36 indices)
  // This MAX_FRAME should ideally come from currentModelData if different models have different frame counts.
  // For now, assuming all 360 models have 37 frames (0-36).
  const MAX_FRAME = 36;

  const [color, setColor] = useState(currentModelData.colors[0]);
  const [frame, setFrame] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const dragStartX = useRef(null);

  const sensitivity = 10; // Drag sensitivity (pixels to move 1 frame)

  // Reset state when model changes
  useEffect(() => {
    setColor(currentModelData.colors[0]);
    setFrame(0);
  }, [currentModelData]);

  /* -------------------------------
     Drag logic
  -------------------------------- */
  const updateFrame = (deltaX) => {
    if (!is360) return; // No rotation for static models

    setFrame(prev => {
      let change = Math.floor(deltaX / sensitivity);
      let next = prev - change; // Invert direction for natural feel

      // Wrap logic for 0 to MAX_FRAME (inclusive)
      const numFrames = MAX_FRAME + 1; // Total number of frames (0 to 36 is 37 frames)
      next = (next % numFrames + numFrames) % numFrames;

      return next;
    });
  };

  const onMouseDown = (e) => {
    if (!is360) return;
    dragStartX.current = e.clientX;
    setIsDragging(true);
  };

  const onMouseMove = (e) => {
    if (!is360 || dragStartX.current === null) return;
    const deltaX = e.clientX - dragStartX.current;

    // update only if moved significantly to avoid jitter
    if (Math.abs(deltaX) > 2) {
      updateFrame(deltaX);
      dragStartX.current = e.clientX;
    }
  };

  const onMouseUp = () => {
    dragStartX.current = null;
    setIsDragging(false);
  };

  const onTouchStart = (e) => {
    if (!is360) return;
    dragStartX.current = e.touches[0].clientX;
    setIsDragging(true);
  };

  const onTouchMove = (e) => {
    if (!is360 || dragStartX.current === null) return;
    const deltaX = e.touches[0].clientX - dragStartX.current;

    if (Math.abs(deltaX) > 2) {
      updateFrame(deltaX);
      dragStartX.current = e.touches[0].clientX;
    }
  };

  /* -------------------------------
     Preload images on color change
  -------------------------------- */
  useEffect(() => {
    // Preload all frames for the new color using the pre-generated array
    if (color.images) {
      color.images.forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    }
  }, [color]);

  return (
    <div className="w-full">
      {/* 360 Viewer */}
      <div
        className={`relative w-full mx-auto select-none rounded-xl overflow-hidden`}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onMouseUp}
        style={{
          touchAction: 'pan-y',
          cursor: is360 ? `url(/assets/images/homepage/360.png), auto` : 'default'
        }}
      >
        <div className="aspect-[16/9] w-full bg-gray-100 relative">
          <img
            src={color.images[frame] || color.images[0]}
            alt={`${currentModelData.model} view`}
            draggable={false}
            className="w-full h-full object-contain pointer-events-none"
          />
          {/* Loading / Placeholder state could potentially go here if network is slow, 
                but preloading handles most cases */}
        </div>

        {/* 360 badge / Instruction - Only show if 360 enabled */}
        {is360 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center pointer-events-none">
            <div className="bg-black/70 text-white px-4 py-1.5 rounded-full text-sm font-medium backdrop-blur-sm mb-2">
              {t.modelShowcase.view360}
            </div>
            <p className="text-black/50 text-xs font-semibold uppercase tracking-wider backdrop-blur-[2px]">Drag to rotate</p>
          </div>
        )}
      </div>

      {/* Color selector */}
      <div className="flex justify-center gap-6 mt-8">
        {currentModelData.colors.map(c => (
          <button
            key={c.id}
            onClick={() => {
              setColor(c);
              setFrame(0); // Reset to front view on color change
            }}
            className={`group relative w-12 h-12 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 focus:outline-none
              ${c.id === color.id ? 'ring-4 ring-offset-2 ring-red-600 scale-110' : 'hover:ring-2 hover:ring-offset-2 hover:ring-gray-300'}
            `}
            style={{ backgroundColor: c.swatch }}
            aria-label={`Select ${c.label}`}
          >
            {/* Tooltip */}
            <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-xs font-medium text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              {c.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}